import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { nationalIdText } from '@/lib/national-id';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { get, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { dateOnly } from '@/lib/utils';
import { DataTable, FilterField, PageHeader, SearchFilterBar } from '@/components/ui/layout';
import { Badge, Button, Select } from '@/components/ui/primitives';
import type { PatientSummary } from './PatientPicker';

interface PatientRow extends PatientSummary {
  bloodGroup: string;
  cnic: string | null;
  createdAt: string;
}

/** Master Patient Index search (SRS 6.1). */
export function PatientsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');

  const patients = useQuery({
    queryKey: ['patients', search, category],
    queryFn: () =>
      get<{ data: PatientRow[] }>(`/patients${qs({ q: search || undefined, category: category || undefined, limit: 50 })}`),
  });

  return (
    <>
      <PageHeader
        title={tr('opd', 'patients')}
        description={tr('opd', 'search_by_mrn_name_phone_or_x', { label: nationalIdText().label })}
        actions={
          can('patient.register') && (
            <Link to="/patients/new">
              <Button>{tr('opd', 'register_patient')}</Button>
            </Link>
          )
        }
      />

      <SearchFilterBar
        search={{ value: search, onChange: setSearch, placeholder: tr('opd', 'start_typing_to_search'), autoFocus: true }}
        filters={
          <FilterField label={tr('opd', 'category')}>
            <Select value={category} onChange={(e) => setCategory(e.target.value)} className="w-48">
              <option value="">{tr('opd', 'all_categories')}</option>
              {['general', 'panel', 'insurance', 'staff', 'charity', 'vip'].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </FilterField>
        }
        onReset={
          search || category
            ? () => {
                setSearch('');
                setCategory('');
              }
            : undefined
        }
      />

      <DataTable
        rows={patients.data?.data}
        loading={patients.isLoading} refreshing={patients.isFetching}
        error={patients.error}
        rowKey={(row) => row.id}
        onRowClick={(row) => navigate(`/patients/${row.id}`)}
        empty={search ? tr('opd', 'no_patient_matches_that_search') : tr('opd', 'search_above_or_register_a_new_patient')}
        columns={[
          { header: tr('opd', 'mrn'), width: '150px', sort: (row) => row.mrn, cell: (row) => <span className="tabular font-medium">{row.mrn}</span> },
          {
            header: tr('opd', 'name'),
            sort: (row) => row.fullName,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.fullName}</p>
                <p className="text-xs text-muted-foreground">
                  {row.ageLabel} · {row.sex}
                </p>
              </div>
            ),
          },
          { header: tr('opd', 'phone'), sort: (row) => row.phone, cell: (row) => row.phone ?? '—' },
          { header: nationalIdText().label, sort: (row) => row.cnic, cell: (row) => row.cnic ?? '—' },
          { header: tr('opd', 'category'), sort: (row) => row.category, cell: (row) => <Badge variant="secondary">{row.category}</Badge> },
          { header: tr('opd', 'registered'), sort: (row) => row.createdAt, cell: (row) => dateOnly(row.createdAt) },
        ]}
      />
    </>
  );
}
